/**
 * Elenco e caricamento dei documenti di un record acquisti (contratto, offerta).
 *
 * `base` e' l'endpoint della collezione documenti del record, es.
 * `/api/acquisti/contratti/<id>/documenti`: GET elenca, POST carica, GET `/<fileId>` scarica.
 * I file stanno sulla libreria SharePoint DocumentiAcquisti e passano sempre dal backend: nessun
 * link diretto a SharePoint, perche' chi usa il portale non deve avere accesso alla libreria.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@shared/services/api";
import { useToast } from "@shared/components/ui/Toast";
import { caricaFile, apriFile } from "../services/file";
import { dataIt } from "../utils/formato";

function dimensione(byte) {
  if (!byte && byte !== 0) return "";
  if (byte < 1024 * 1024) return `${Math.max(1, Math.round(byte / 1024))} KB`;
  return `${(byte / 1024 / 1024).toFixed(1)} MB`;
}

export default function Documenti({ base, puoScrivere, titolo = "Documenti" }) {
  const { showToast } = useToast();
  const [file, setFile] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState(null);
  const input = useRef(null);

  const ricarica = useCallback(async () => {
    setCaricamento(true);
    try {
      const res = await api.get(base);
      setFile(res.value || []);
      setErrore(null);
    } catch (e) {
      setErrore(e.message);
    } finally {
      setCaricamento(false);
    }
  }, [base]);

  useEffect(() => {
    ricarica();
  }, [ricarica]);

  async function onScelto(e) {
    const scelto = e.target.files?.[0];
    e.target.value = "";
    if (!scelto) return;
    setInvio(true);
    try {
      await caricaFile(base, scelto);
      showToast("Documento caricato", "success");
      await ricarica();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setInvio(false);
    }
  }

  return (
    <section className="card space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold text-gray-900">{titolo}</h2>
        {puoScrivere && (
          <>
            <input
              ref={input}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              onChange={onScelto}
            />
            <button
              type="button"
              className="btn btn-secondary text-sm"
              disabled={invio}
              onClick={() => input.current?.click()}
            >
              {invio ? "Caricamento…" : "Carica documento"}
            </button>
          </>
        )}
      </div>

      {errore && <p className="text-sm text-red-700">{errore}</p>}
      {caricamento ? (
        <p className="text-sm text-gray-500">Caricamento…</p>
      ) : file.length === 0 ? (
        <p className="text-sm text-gray-500">Nessun documento caricato.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {file.map((f) => (
            <li
              key={f.id}
              className="flex items-center justify-between gap-3 py-2 text-sm"
            >
              <button
                type="button"
                className="text-acquisti-700 hover:underline text-left break-all"
                onClick={() =>
                  apriFile(`${base}/${f.id}`, f.nome).catch((err) =>
                    showToast(err.message, "error"),
                  )
                }
              >
                {f.nome}
              </button>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {dimensione(f.dimensione)} · {dataIt(f.caricatoIl)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
